import * as FC from "@fluentui/react-components";
import * as IC from "@fluentui/react-icons";
import * as FDC from "@fluentui/react-datepicker-compat";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSearchInput } from "../../components/search_input";
import { useDrawer } from "../../components/use_drawer";
import { bindInputValue, useInfo } from "../../form_utils";
import {
    AttachList,
    AttachDropdown,
    ModelLeafDropDown,
} from "../../services/model_leaf_service";
import { TurbineSelector } from "../../services/turbine_service";
import { DeleteButton } from "../../components/delete_button";
import { Context } from "../../context";
import { SimpleGrid } from "../../components/simple_grid";
import { LeafDef } from "../../services/leaf_service";
import { toDate, toDateString } from "../../date_utils";

const useStyles = FC.makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("10px"),
        ...FC.shorthands.padding("10px"),
    },
    form: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("10px"),
    },
});

const toNumericId = (value) => {
    if (value == null) {
        return null;
    }

    if (typeof value === "object") {
        return toNumericId(value.id ?? value.rid);
    }

    const normalized =
        typeof value === "string" && value.includes(":")
            ? value.split(":").pop()
            : value;
    const numericValue = Number(normalized);
    return Number.isNaN(numericValue) ? null : numericValue;
};

const normalizeAttachId = (value) =>
    value == null ? "" : value.toString().trim().toUpperCase();

const getLeafTurbineId = (leaf) =>
    toNumericId(leaf?.turbineId ?? leaf?.turbine?.id ?? leaf?.turbine);

const getTurbineModelId = (turbine) =>
    toNumericId(turbine?.modelId ?? turbine?.model?.id);

const buildTurbineModelGroups = (turbines, leaves) => {
    const attachIdsByTurbine = new Map();

    leaves.forEach((leaf) => {
        const turbineId = getLeafTurbineId(leaf);
        const attachId = normalizeAttachId(leaf?.attach_id ?? leaf?.attachId);
        if (!turbineId || !AttachList.includes(attachId)) {
            return;
        }

        if (!attachIdsByTurbine.has(turbineId)) {
            attachIdsByTurbine.set(turbineId, new Set());
        }
        attachIdsByTurbine.get(turbineId).add(attachId);
    });

    const groupsByModel = new Map();

    turbines.forEach((turbine) => {
        const turbineModelId = getTurbineModelId(turbine);
        if (!turbineModelId) {
            return;
        }

        const turbineId = toNumericId(turbine.id);
        const existingAttachIds = attachIdsByTurbine.get(turbineId) ?? new Set();
        const missingAttachIds = AttachList.filter((attachId) => !existingAttachIds.has(attachId));

        if (!groupsByModel.has(turbineModelId)) {
            groupsByModel.set(turbineModelId, {
                turbineModelId,
                turbineModelSeq: turbine.model?.seq || `型号 ${turbineModelId}`,
                turbines: [],
                turbineCount: 0,
                completeTurbineCount: 0,
                existingCount: 0,
                missingCount: 0,
            });
        }

        const group = groupsByModel.get(turbineModelId);
        group.turbineCount += 1;
        group.existingCount += AttachList.length - missingAttachIds.length;
        group.missingCount += missingAttachIds.length;
        if (missingAttachIds.length === 0) {
            group.completeTurbineCount += 1;
        }
        group.turbines.push({
            ...turbine,
            missingAttachIds,
        });
    });

    return Array.from(groupsByModel.values()).sort((left, right) =>
        left.turbineModelSeq.localeCompare(right.turbineModelSeq, "zh-Hans-CN", {
            numeric: true,
        }),
    );
};

export const formatBatchLeafModelLabel = (model) => {
    if (!model) {
        return "";
    }

    // FieldItem is catalogue provenance, not ownership or availability. Showing it here
    // made a globally reusable model look tied to the field that first entered it.
    return model.length ? `${model.seq} (${model.length}m)` : model.seq;
};

const LeafModelDropdown = ({ modelLeaves, value, onChange, disabled }) => {
    const selectedModel = modelLeaves.find((model) => toNumericId(model.id) === toNumericId(value));

    return (
        <FC.Dropdown
            placeholder="选择叶片型号"
            selectedOptions={value ? [String(value)] : []}
            value={formatBatchLeafModelLabel(selectedModel)}
            disabled={disabled}
            onOptionSelect={(_, data) => {
                const leafModelId = toNumericId(data.optionValue);
                onChange(leafModelId);
            }}
            style={{ minWidth: "180px" }}
        >
            {modelLeaves.map((model) => (
                <FC.Option key={model.id} value={String(model.id)}>
                    {formatBatchLeafModelLabel(model)}
                </FC.Option>
            ))}
        </FC.Dropdown>
    );
};

// Blade models and their manufacturers are global catalogue entries. Only the physical
// blade-to-turbine relationship determines the blade's field.
const LEAF_WRITE_ERROR_MESSAGES = {
    LeafIdentityRequired: "请填写叶片编号并选择叶片型号。",
    LeafRelationFieldMismatch:
        "所选风机不属于当前风场，或所选叶片型号已不存在。请刷新后重新选择。",
    FieldItemNotFound: "当前风场不存在或已停用，请返回风场列表重新选择。",
};

export const describeLeafWriteError = (error) => {
    const code = error?.errorCode || error?.message;
    return LEAF_WRITE_ERROR_MESSAGES[code] || error?.message || String(error ?? "未知错误");
};

const LeafForm = ({ item, onChange }) => {
    const { apiClient, currentField, user } = useContext(Context);
    const fieldId = toNumericId(currentField?.id);
    // Leaf writes resolve their target tenant from ?tenantId=; the backend refuses to guess
    // one, so omitting it makes every create/update/delete a 400 TenantIdRequired.
    const tenantId = toNumericId(user?.tenantId);

    const { info, setInfo, updateInfo } = useInfo(null);
    const v = bindInputValue(item, info, setInfo);
    const doDelete = async () => {
        try {
            await apiClient.deleteLeaf(item.id, tenantId);
            onChange();
        } catch (error) {
            console.error("Failed to delete leaf:", error);
            alert("删除失败: " + (error.message || error));
        }
    };
    const doSave = async () => {
        let payload;
        try {
            if (item) {
                // Update existing leaf — only send fields that changed
                payload = {};

                if (info?.seq !== undefined) {
                    payload.Seq = info.seq;
                }

                if (info?.attach_id !== undefined || info?.attachId !== undefined) {
                    payload.AttachId = info.attach_id ?? info.attachId;
                }

                if (info?.turbine !== undefined) {
                    const turbineId = Number(
                        typeof info.turbine === 'object' ? info.turbine?.id : info.turbine
                    );
                    if (Number.isNaN(turbineId)) {
                        alert("请选择有效的风机");
                        return;
                    }
                    payload.TurbineId = turbineId;
                }

                if (info?.model !== undefined) {
                    const modelId = Number(
                        typeof info.model === 'object' ? info.model?.id : info.model
                    );
                    if (Number.isNaN(modelId)) {
                        alert("请选择有效的叶片型号");
                        return;
                    }
                    payload.ModelId = modelId;
                }

                if (info?.installed_at !== undefined || info?.installedAt !== undefined) {
                    const installedAt = info?.installed_at ?? info?.installedAt;
                    payload.InstalledAt =
                        installedAt instanceof Date ? installedAt.toISOString() : installedAt;
                }

                if (Object.keys(payload).length === 0) {
                    alert("请修改后再保存。");
                    return;
                }

                await apiClient.updateLeaf(item.id, payload, tenantId);
                onChange();
            } else {
                // Create new leaf
                const rawTurbineId =
                    info?.turbine && typeof info.turbine === 'object'
                        ? info.turbine.id
                        : info?.turbine;
                const rawModelId =
                    info?.model && typeof info.model === 'object'
                        ? info.model.id
                        : info?.model;

                payload = {
                    Seq: info?.seq || '',
                    AttachId: info?.attach_id || info?.attachId,
                    TurbineId: rawTurbineId != null ? Number(rawTurbineId) : null,
                    ModelId: rawModelId != null ? Number(rawModelId) : null,
                    InstalledAt:
                        info?.installed_at instanceof Date
                            ? info.installed_at.toISOString()
                            : info?.installedAt || null,
                };

                console.log("Creating leaf with data:", payload);

                // Validate required fields
                if (!payload.TurbineId || Number.isNaN(payload.TurbineId)) {
                    alert("请选择风机");
                    return;
                }
                if (!payload.ModelId || Number.isNaN(payload.ModelId)) {
                    alert("请选择叶片型号");
                    return;
                }

                await apiClient.createLeaf(payload, tenantId);
                onChange();
            }
        } catch (error) {
            console.error("Failed to save leaf:", error);
            console.error("Data being sent:", payload);
            if (error.message && error.message.includes('404')) {
                alert("叶片功能暂时不可用 - 后端API尚未实现。请联系开发团队。");
            } else {
                alert("保存失败: " + describeLeafWriteError(error));
            }
        }
    };

    const styles = useStyles();
    return (
        <div className={FC.mergeClasses(styles.form, "")}>
            <FC.Input contentBefore={"编号"} {...v("seq")} />
            <FC.Label>叶片型号</FC.Label>
            {/* No fieldId — the blade-model catalogue is globally shared. See turbine.js. */}
            <ModelLeafDropDown
                defaultValue={item?.model?.seq}
                defaultSelectedOptions={
                    item?.model?.id ?? item?.modelId
                        ? [String(item?.model?.id ?? item?.modelId)]
                        : undefined
                }
                onOptionSelect={(evt, data) =>
                    updateInfo({
                        model: Number.isNaN(Number(data.optionValue))
                            ? data.optionValue
                            : Number(data.optionValue),
                    })
                }
            />
            <FC.Label>挂载风机</FC.Label>
            <TurbineSelector
                defaultValue={item?.turbine?.name}
                onChange={(t) => {
                    console.log("selected turbine:", t);
                    const numericId = Number(t);
                    updateInfo({
                        turbine: Number.isNaN(numericId) ? t : numericId,
                    });
                }}
            />

            <FC.Label>挂载点位</FC.Label>
            <AttachDropdown
                defaultValue={item?.attach_id}
                onOptionSelect={(evt, data) =>
                    updateInfo({ attach_id: data.optionValue })
                }
            />
            <FC.Label>挂载时间</FC.Label>
            <FDC.DatePicker
                formatDate={toDateString}
                value={
                    toDate(info?.installed_at) ||
                    toDate(item?.installed_at) ||
                    null
                }
                onSelectDate={(date) => {
                    updateInfo({ installed_at: date });
                }}
            />
            <FC.Divider />

            {item && <DeleteButton onClick={doDelete} />}
            <FC.Button
                onClick={doSave}
                disabled={Object.keys(info ?? {}).length === 0}
                appearance="primary"
            >
                {item ? "保存" : "创建"}
            </FC.Button>
        </div>
    );
};

const BatchLeafForm = ({ onChange }) => {
    const { apiClient, currentField, user } = useContext(Context);
    const [turbines, setTurbines] = useState([]);
    const [leaves, setLeaves] = useState([]);
    const [modelLeaves, setModelLeaves] = useState([]);
    const [selectedLeafModels, setSelectedLeafModels] = useState({});
    const [installedAt, setInstalledAt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const fieldId = toNumericId(currentField?.id);
    const groups = useMemo(
        () => buildTurbineModelGroups(turbines, leaves),
        [turbines, leaves],
    );
    const groupsNeedingCreate = useMemo(
        () => groups.filter((group) => group.missingCount > 0),
        [groups],
    );
    const missingSelectionCount = groupsNeedingCreate.filter(
        (group) => !selectedLeafModels[group.turbineModelId],
    ).length;
    const totalMissingCount = groupsNeedingCreate.reduce(
        (sum, group) => sum + group.missingCount,
        0,
    );

    const loadPlan = useCallback(async () => {
        if (!apiClient || !fieldId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setLoadError(null);
        try {
            const [nextTurbines, nextLeaves, nextModelLeaves] = await Promise.all([
                apiClient.getTurbines({ fieldId }),
                apiClient.getLeavesByField(fieldId),
                // Unscoped: the blade-model catalogue is globally shared, so this field may use
                // any model. The "exactly one model, auto-select it" shortcut below now only
                // fires when the whole catalogue holds one model, which is the only case where
                // there is nothing to choose.
                apiClient.getModelLeaves(),
            ]);

            setTurbines(nextTurbines || []);
            setLeaves(nextLeaves || []);
            setModelLeaves(nextModelLeaves || []);
        } catch (error) {
            console.error("Failed to load blade batch plan:", error);
            setLoadError(error.message || "加载失败");
            setTurbines([]);
            setLeaves([]);
            setModelLeaves([]);
        } finally {
            setLoading(false);
        }
    }, [apiClient, fieldId]);

    useEffect(() => {
        loadPlan();
    }, [loadPlan]);

    useEffect(() => {
        if (modelLeaves.length !== 1 || groupsNeedingCreate.length === 0) {
            return;
        }

        const onlyModelId = toNumericId(modelLeaves[0].id);
        setSelectedLeafModels((prev) => {
            const next = { ...prev };
            let changed = false;
            groupsNeedingCreate.forEach((group) => {
                if (!next[group.turbineModelId]) {
                    next[group.turbineModelId] = onlyModelId;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [modelLeaves, groupsNeedingCreate]);

    const doSave = async () => {
        if (!fieldId) {
            alert("请先选择一个风场");
            return;
        }

        const items = groupsNeedingCreate.map((group) => ({
            TurbineModelId: group.turbineModelId,
            LeafModelId: toNumericId(selectedLeafModels[group.turbineModelId]),
        }));

        const invalidItem = items.find((item) => !item.LeafModelId);
        if (invalidItem) {
            alert("请为每一种风机型号选择叶片型号");
            return;
        }

        setSaving(true);
        try {
            const result = await apiClient.batchCreateLeavesByTurbineModel(
                {
                    FieldId: fieldId,
                    Items: items,
                    InstalledAt: installedAt ? installedAt.toISOString() : null,
                },
                toNumericId(user?.tenantId),
            );

            const createdLeafCount = result?.createdLeafCount ?? 0;
            const skippedLeafCount = result?.skippedLeafCount ?? 0;
            alert(`创建完成：新增 ${createdLeafCount} 个叶片，跳过 ${skippedLeafCount} 个已有叶片。`);
            onChange();
        } catch (error) {
            console.error("Failed to batch create leaves:", error);
            alert("批量创建失败: " + (error.message || error));
        } finally {
            setSaving(false);
        }
    };

    const styles = useStyles();

    if (!fieldId) {
        return <FC.Text>请先选择一个风场</FC.Text>;
    }

    if (loading) {
        return <FC.Spinner label="正在读取风机和叶片数据..." />;
    }

    if (loadError) {
        return (
            <div className={styles.form}>
                <FC.Text>加载失败: {loadError}</FC.Text>
                <FC.Button onClick={loadPlan}>重新加载</FC.Button>
            </div>
        );
    }

    return (
        <div className={styles.form}>
            <FC.Text weight="semibold">当前风场：{currentField?.name || "-"}</FC.Text>
            <FC.Text size={200}>
                共 {turbines.length} 台风机，{groups.length} 种风机型号；待补齐 {totalMissingCount} 个叶片。
            </FC.Text>

            <FC.Field label="挂载时间">
                <FDC.DatePicker
                    formatDate={toDateString}
                    value={installedAt}
                    onSelectDate={(date) => setInstalledAt(date)}
                />
            </FC.Field>

            <div
                style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 4,
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1.2fr 0.8fr 1fr 1fr",
                        gap: 8,
                        padding: "8px 10px",
                        background: "#f5f5f5",
                        fontWeight: 600,
                    }}
                >
                    <span>风机型号</span>
                    <span>风机数量</span>
                    <span>叶片状态</span>
                    <span>叶片型号</span>
                </div>
                {groups.map((group) => {
                    const complete = group.missingCount === 0;

                    return (
                        <div
                            key={group.turbineModelId}
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1.2fr 0.8fr 1fr 1fr",
                                gap: 8,
                                alignItems: "center",
                                padding: "10px",
                                borderTop: "1px solid #e0e0e0",
                            }}
                        >
                            <div>
                                <FC.Text weight="semibold">{group.turbineModelSeq}</FC.Text>
                            </div>
                            <div>{group.turbineCount} 台</div>
                            <div>
                                {complete
                                    ? "已完整"
                                    : `缺 ${group.missingCount} 个，已完整 ${group.completeTurbineCount} 台`}
                            </div>
                            <LeafModelDropdown
                                modelLeaves={modelLeaves}
                                value={selectedLeafModels[group.turbineModelId]}
                                disabled={complete || saving}
                                onChange={(leafModelId) =>
                                    setSelectedLeafModels((prev) => ({
                                        ...prev,
                                        [group.turbineModelId]: leafModelId,
                                    }))
                                }
                            />
                        </div>
                    );
                })}
                {groups.length === 0 && (
                    <div style={{ padding: 12 }}>
                        <FC.Text>当前风场没有可用风机。</FC.Text>
                    </div>
                )}
            </div>

            {modelLeaves.length === 0 && (
                <FC.Text size={200}>
                    系统中还没有叶片型号，请先在业务配置里创建（型号为全局共享，创建一次所有风场可用）。
                </FC.Text>
            )}

            <FC.Divider />
            <FC.Button
                appearance="primary"
                onClick={doSave}
                disabled={
                    saving ||
                    modelLeaves.length === 0 ||
                    groupsNeedingCreate.length === 0 ||
                    missingSelectionCount > 0
                }
            >
                {saving ? "创建中..." : "批量创建 A/B/C 叶片"}
            </FC.Button>
        </div>
    );
};

export const LeafManagement = () => {
    const { filter, SearchInput } = useSearchInput({
        placeholder: "按风机名称搜索...",
    });
    const [items, setItems] = useState([]);
    const { apiClient, currentField } = useContext(Context);
    let { openDrawer, closeDrawer, drawer } = useDrawer({});
    const onNewLeaf = () => {
        openDrawer({
            header: "批量创建叶片",
            body: <BatchLeafForm onChange={onFormChange} />,
        });
    };
    const loadLeafs = useCallback(async () => {
        try {
            // Filter leaves by current field
            const fieldId = currentField?.id || null;
            const items = await apiClient.getLeavesByField(fieldId, filter);
            setItems(items || []);
        } catch (error) {
            console.error("Failed to load leaves:", error);
            if (error.message && error.message.includes('404')) {
                // Backend API not implemented yet
                console.warn("Leaf API not implemented in backend. Showing empty list.");
            }
            setItems([]);
        }
    }, [apiClient, filter, currentField]);
    const onFormChange = () => {
        loadLeafs();
        closeDrawer();
    };
    useEffect(() => {
        if (!apiClient) {
            return;
        }
        loadLeafs();
    }, [apiClient, currentField, loadLeafs]);

    const columns = [
        LeafDef.createTableColumn("seq"),
        LeafDef.createTableColumn("model"),
        LeafDef.createTableColumn("turbine"),
        LeafDef.createTableColumn("attach_id"),
        LeafDef.createTableColumn("installed_at"),
        LeafDef.createOpsColumn({
            renderCell: (item) => {
                const onClick = () => {
                    openDrawer({
                        header: "叶片详情",
                        body: <LeafForm item={item} onChange={onFormChange} />,
                    });
                };
                return (
                    <FC.TableCellLayout>
                        <FC.Button
                            appearance="subtle"
                            icon={<IC.MoreCircleFilled />}
                            onClick={onClick}
                        />
                    </FC.TableCellLayout>
                );
            },
        }),
    ];

    const styles = useStyles();
    return (
        <div className={styles.root}>
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 10,
                    alignItems: "center",
                }}
            >
                <FC.Label>快速检索</FC.Label>
                <SearchInput />
                <FC.Button appearance="primary" onClick={onNewLeaf}>
                    批量创建叶片
                </FC.Button>
            </div>
            <SimpleGrid items={items} columns={columns} />
            {drawer}
        </div>
    );
};
